// Amigos no aparelho: as chamadas à API. A API confere tudo (só conta Google, limites, bloqueios); o aparelho só
// mostra o estado que ela devolve.
import type { Confronto, PerfilNaGamificacao } from '@truco6/gamificacao';
import type { Plano } from '@truco6/planos';
import { api } from './api.ts';
import type { Sessao } from './conta.ts';

export type Relacao = 'nenhum' | 'pedido_enviado' | 'pedido_recebido' | 'amigos' | 'bloqueado' | 'indisponivel';
export interface Amigo { usuario: string; apelido: string; avatar: string | null; desde: number }
export interface PedidoDeAmizade { id: string; usuario: string; apelido: string; avatar: string | null; em: number }
export interface Encontrado { usuario: string; apelido: string; avatar: string | null; relacao: Relacao }
// Status de cada amigo (a API calcula e manda só para os amigos), já na ordem: em sala aberta, jogando agora, online e
// visto há. Sala privada vem só como "jogando", sem nome nem código.
export type StatusAmigo =
  | { tipo: 'sala'; codigo: string; nome: string; sentados: number; vaga: boolean; vagas?: number } // vagas: partida andando com vaga aberta
  | { tipo: 'jogando'; nome: string | null; assistir?: 'publica' | 'privada' } // assistir: a sala aceita plateia
  | { tipo: 'online' }
  | { tipo: 'visto'; em: number | null };
export interface AmigoNaLista extends Amigo {
  nivel: number | null;
  divisao: { divisao: number; pd: number } | null;
  trio: { id: string; nome: string; escudo: string } | null;
  status: StatusAmigo;
  whatsapp: boolean; // vocês dois mostram o número: o botão abre a conversa
}
// A sala em que você está agora: o Chamar vale com ela na espera e com vaga.
export interface MinhaSala { codigo: string; nome: string; privada: boolean; chamavel: boolean }

const id = (u: string) => encodeURIComponent(u);
export const lerAmigos = (s: Sessao) => api<{ amigos: AmigoNaLista[]; eu: { sala: MinhaSala | null; whatsapp: boolean } }>(s, 'GET', '/amigos');
export const chamarParaMesa = (s: Sessao, u: string) => api<{ ok: true; ate: number }>(s, 'POST', `/amigos/${id(u)}/chamar`);
export const lerPedidos = (s: Sessao) => api<{ recebidos: PedidoDeAmizade[]; enviados: PedidoDeAmizade[] }>(s, 'GET', '/amigos/pedidos');
export const pedirAmizade = (s: Sessao, para: string) => api<{ estado: Relacao }>(s, 'POST', '/amigos/pedidos', { para });
export const aceitarPedido = (s: Sessao, pedido: string) => api<{ estado: Relacao }>(s, 'POST', `/amigos/pedidos/${id(pedido)}/aceitar`);
export const recusarPedido = (s: Sessao, pedido: string) => api<{ estado: Relacao }>(s, 'POST', `/amigos/pedidos/${id(pedido)}/recusar`);
export const desfazerPedido = (s: Sessao, pedido: string) => api<{ estado: Relacao }>(s, 'DELETE', `/amigos/pedidos/${id(pedido)}`);
export const desfazerAmizade = (s: Sessao, u: string) => api<{ estado: Relacao }>(s, 'DELETE', `/amigos/${id(u)}`);
export const bloquear = (s: Sessao, u: string) => api<{ estado: Relacao }>(s, 'POST', '/bloqueios', { usuario: u });
export const desbloquear = (s: Sessao, u: string) => api<{ estado: Relacao }>(s, 'DELETE', `/bloqueios/${id(u)}`);
export const lerBloqueados = (s: Sessao) => api<{ bloqueados: { usuario: string; apelido: string; avatar: string | null; em: number }[] }>(s, 'GET', '/bloqueios');
export const lerRelacao = (s: Sessao, u: string) => api<{ relacao: Relacao }>(s, 'GET', `/jogadores/${id(u)}/relacao`);
export const lerRelacoes = (s: Sessao, ids: string[]) => api<{ relacoes: Record<string, Relacao> }>(s, 'POST', '/jogadores/relacoes', { ids });
export const buscarJogadores = (s: Sessao, apelido: string) =>
  api<{ jogadores: Encontrado[] }>(s, 'GET', `/jogadores/busca?apelido=${encodeURIComponent(apelido)}`);

// Quem criou a conta e entrou pela primeira vez na sala de alguém vira amigo do dono, quando a conta já é Google.
// A API confere tudo e faz a amizade uma vez só. O aparelho pergunta até a resposta ser final (a primeira sala já
// foi vista) ou por 2 dias, o que vier antes.
const CHAVE_ORIGEM = 'truco6:amizade-origem:';
const TENTAR_POR_MS = 2 * 86_400_000;
export async function amizadeDaOrigem(s: Sessao): Promise<string | null> {
  if (s.anonimo) return null;
  let desde = Date.now();
  try {
    const v = JSON.parse(localStorage.getItem(CHAVE_ORIGEM + s.id) ?? 'null') as { final?: boolean; desde?: number } | null;
    if (v?.final || (typeof v?.desde === 'number' && Date.now() - v.desde > TENTAR_POR_MS)) return null;
    if (typeof v?.desde === 'number') desde = v.desde;
  } catch { /* sem armazenamento: pergunta de novo */ }
  try {
    const r = await api<{ amigo: string | null; final: boolean }>(s, 'POST', '/amigos/origem');
    try { localStorage.setItem(CHAVE_ORIGEM + s.id, JSON.stringify({ final: r.final, desde })); } catch { /* nada a fazer */ }
    return r.amigo;
  } catch {
    return null; // sem conexão: tenta na próxima vez
  }
}

// Notificações: pedido de amizade recebido, pedido aceito, chamado para a mesa e convite de trio.
export type TipoNotificacao = 'amizade_pedido' | 'amizade_aceita' | 'chamado_mesa' | 'convite_trio'
  | 'comunidade_convite' | 'comunidade_evento' | 'comunidade_aviso'
  | 'campeonato_convite' | 'campeonato_aviso' | 'campeonato_jogo';
export interface Notificacao {
  id: string;
  tipo: TipoNotificacao;
  de: { usuario: string; apelido: string; avatar: string | null } | null;
  dados: Record<string, unknown>;
  criadaEm: number;
  lida: boolean;
}
export const lerNotificacoes = (s: Sessao) => api<{ notificacoes: Notificacao[] }>(s, 'GET', '/notificacoes');
export const lerTodasAsNotificacoes = (s: Sessao) => api<{ ok: true }>(s, 'POST', '/notificacoes/lidas');

// WhatsApp entre amigos: a sua chave (o número só fica guardado com ela ligada) e o número de um amigo, que a API só
// entrega quando vocês são amigos e os dois mostram o número.
export interface MeuContato { ligado: boolean; whatsapp: string | null; consentidoEm: number | null }
export const lerMeuContato = (s: Sessao) => api<MeuContato>(s, 'GET', '/eu/contato');
// Liga a chave com o celular da conta; sem celular na conta, o número informado aqui vira o celular da conta.
export const mostrarMeuWhatsapp = (s: Sessao, whatsapp?: string) =>
  api<MeuContato>(s, 'POST', '/eu/contato', whatsapp === undefined ? { consentimento: true } : { whatsapp, consentimento: true });
export const esconderMeuWhatsapp = (s: Sessao) => api<MeuContato>(s, 'DELETE', '/eu/contato');
export const lerWhatsappDoAmigo = (s: Sessao, u: string) => api<{ whatsapp: string; link: string }>(s, 'GET', `/amigos/${id(u)}/whatsapp`);

// 5511987654321 vira "(11) 98765-4321".
export function formatarWhatsapp(n: string): string {
  const d = n.replace(/^55/, '');
  return `(${d.slice(0, 2)}) ${d.slice(2, d.length - 4)}-${d.slice(-4)}`;
}

// Perfil público: a lista fechada de campos que a API monta. Bloqueio: "Perfil indisponível". Convidado: só apelido,
// rosto e o selo (o retrospecto vem à parte).
export interface PerfilPublico {
  usuario: string; apelido: string; avatar: string | null; convidado: false;
  nivel: number; plano: Plano; divisao: PerfilNaGamificacao['divisao']; trioPrincipal: PerfilNaGamificacao['trioPrincipal'];
  cidade: { nome: string; uf: string } | null; desde: string; ofensiva: number;
  partidas: number; vitorias: number; aproveitamento: number;
  badges: PerfilNaGamificacao['badges']; liga: PerfilNaGamificacao['liga']; trios: PerfilNaGamificacao['trios']; lances: PerfilNaGamificacao['lances'];
  coroa?: boolean; // a coroa do Rei do Pote da semana passada
  contraVoce: Confronto | null;
}
export type RespostaDoPerfil = PerfilPublico | { usuario: string; apelido: string; avatar: string | null; convidado: true } | { indisponivel: true };
export const lerPerfilPublico = (s: Sessao, u: string) => api<RespostaDoPerfil>(s, 'GET', `/jogadores/${id(u)}/perfil`);
export type MotivoDenuncia = 'apelido' | 'rosto' | 'comportamento';
export const reportarJogador = (s: Sessao, u: string, motivo: MotivoDenuncia) => api<{ ok: true }>(s, 'POST', `/jogadores/${id(u)}/reportar`, { motivo });

// Chamar para o trio: os seus trios com o estado de cada um para a pessoa, a linha do plano e se dá para criar.
export type EstadoNoTrio = 'convidar' | 'convidado' | 'completo' | 'ja_esta' | 'peca_ao_capitao';
export interface TrioParaChamar { id: string; nome: string; escudo: string; membros: number; estado: EstadoNoTrio }
export interface FolhaDoTrio {
  trios: TrioParaChamar[];
  plano: { nome: string; limite: number; emUso: number };
  criar: 'pode' | 'sem_plano' | 'limite';
  mensagemLimite: string | null;
}
export const lerChamarParaTrio = (s: Sessao, u: string) => api<FolhaDoTrio>(s, 'GET', `/trios/chamar/${id(u)}`);
export const convidarParaTrio = (s: Sessao, trio: string, para: string) => api<{ ok: true }>(s, 'POST', `/trios/${id(trio)}/convites`, { para });
export const lerConvitesDoTrio = (s: Sessao, trio: string) => api<{ pendentes: string[] }>(s, 'GET', `/trios/${id(trio)}/convites`);
export const aceitarConviteDeTrio = (s: Sessao, convite: string, perfil: { apelido: string; avatar: string | null }) =>
  api<{ trio: { id: string; nome: string } }>(s, 'POST', `/trios/convites/${id(convite)}/aceitar`, { perfil });
export const recusarConviteDeTrio = (s: Sessao, convite: string) => api<{ ok: true }>(s, 'POST', `/trios/convites/${id(convite)}/recusar`);
